// src/pages/Procurement/ProcurementGmBriefPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/axios";
import { toast } from "react-hot-toast";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const ROUTES = {
  PROCUREMENT_DASH: "/procurement",
  PROCUREMENT_AI_INSIGHTS: "/procurement/ai-insights",
  PO_RECEIVING_DASH: "/po-receiving-dashboard",
  PO_MANAGEMENT: "/po-management",
  INVOICES: "/invoices",
  VENDORS: "/vendors",
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "£0.00";
  return n.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const arr = (value) => (Array.isArray(value) ? value : []);

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function StatCard({ title, value, sub, tone = "cyan" }) {
  const toneClass =
    tone === "red"
      ? "border-red-500/40 bg-red-950/30 text-red-100"
      : tone === "orange"
        ? "border-orange-500/40 bg-orange-950/30 text-orange-100"
        : tone === "emerald"
          ? "border-emerald-500/40 bg-emerald-950/30 text-emerald-100"
          : tone === "purple"
            ? "border-purple-500/40 bg-purple-950/30 text-purple-100"
            : "border-cyan-500/40 bg-cyan-950/30 text-cyan-100";

  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <p className="text-xs opacity-75">{title}</p>
      <p className="text-3xl font-black mt-1">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-950/80 p-5 shadow-xl shadow-cyan-950/10">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-cyan-100">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function Table({ rows, columns, empty }) {
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-4 text-emerald-100 text-sm">
        {empty}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-slate-400">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-3 py-2 text-left font-semibold">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.po_id || row.vendor_id || index} className="border-t border-slate-800">
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 text-slate-200">
                  {col.render ? col.render(row) : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProcurementGmBriefPage() {
  const { isAdmin, isDeveloper, isManager } = useUserPermissions();

  const canView =
    Boolean(isAdmin) || Boolean(isDeveloper) || Boolean(isManager);

  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const loadBrief = async () => {
    if (!canView) return;

    setLoading(true);

    try {
      const res = await api.get("/ai/procurement/gm-brief", {
        params: { _ts: Date.now() },
      });

      setBrief(res?.data || null);
      setLastLoadedAt(new Date().toISOString());
      toast.success("GM procurement brief refreshed");
    } catch (err) {
      console.error("GM procurement brief failed:", err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load GM procurement brief"
      );
      setBrief(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    loadBrief();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const overview = brief?.overview || {};
  const attention = brief?.attention || {};
  const costCentres = arr(brief?.cost_centres);
  const late = arr(brief?.late_pos);
  const blocked = arr(brief?.blocked_pos);
  const riskyInvoices = arr(brief?.risky_invoices);
  const vendorIssues = arr(brief?.vendor_issues);
  const decisions = arr(brief?.decisions_needed);
  const narrative = arr(brief?.narrative);

  const totalAttention =
    num(attention.late_count) +
    num(attention.blocked_count) +
    num(attention.risky_invoice_count) +
    num(attention.vendor_issue_count) +
    num(attention.decision_count);

  if (!canView) {
    return (
      <div className="min-h-screen p-8 bg-slate-950 text-red-200">
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="mt-2">You do not have access to the GM procurement brief.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 px-6 py-8 text-white">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="rounded-3xl border border-cyan-500/30 bg-slate-950/80 p-6 shadow-2xl shadow-cyan-950/30">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-cyan-300 shadow shadow-cyan-300/70 animate-pulse" />
                <h1 className="text-3xl md:text-5xl font-black text-cyan-100">
                  GM Procurement Brief
                </h1>
              </div>

              <p className="text-slate-400 mt-2 max-w-3xl">
                One-click operational briefing for today’s procurement position, attention items,
                late deliveries, blocked POs, invoice risks, vendor issues, cost centre exposure,
                and decisions needed.
              </p>

              <p className="text-xs text-slate-500 mt-2">
                Last loaded: {lastLoadedAt ? formatDate(lastLoadedAt) : "—"}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadBrief}
                disabled={loading}
                className="rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 px-4 py-2 text-sm font-bold text-white"
              >
                {loading ? "Refreshing…" : "Refresh Brief"}
              </button>

              <Link
                to={ROUTES.PROCUREMENT_AI_INSIGHTS}
                className="rounded-xl border border-cyan-500/50 bg-cyan-950/30 hover:bg-cyan-900/40 px-4 py-2 text-sm font-bold text-cyan-100"
              >
                AI Insights
              </Link>

              <Link
                to={ROUTES.PROCUREMENT_DASH}
                className="rounded-xl border border-slate-700 bg-slate-900 hover:bg-slate-800 px-4 py-2 text-sm font-bold text-slate-100"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </header>

        {loading && !brief ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 text-slate-300">
            Loading GM procurement brief…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <StatCard
                title="Today PO Value"
                value={money(overview.today_po_value)}
                sub={`${num(overview.today_po_count)} PO(s) today`}
                tone="cyan"
              />

              <StatCard
                title="Arriving Today"
                value={num(overview.arriving_today_count)}
                sub={money(overview.arriving_today_value)}
                tone="emerald"
              />

              <StatCard
                title="Pending Approval"
                value={num(overview.pending_approval_count)}
                sub={money(overview.pending_approval_value)}
                tone="orange"
              />

              <StatCard
                title="Attention Items"
                value={totalAttention}
                sub="Late, blocked, invoices, vendors, decisions"
                tone={totalAttention > 0 ? "red" : "emerald"}
              />

              <StatCard
                title="Week Exposure"
                value={money(overview.week_po_value)}
                sub={`${num(overview.week_po_count)} PO(s) this week`}
                tone="purple"
              />
            </div>

            {narrative.length > 0 && (
              <Section title="Executive Summary">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {narrative.map((item, index) => (
                    <div
                      key={`${item.title}-${index}`}
                      className="rounded-xl border border-cyan-700/40 bg-cyan-950/20 p-4"
                    >
                      <h3 className="font-bold text-cyan-100">{item.title}</h3>
                      <p className="text-sm text-slate-300 mt-1">{item.message}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section
              title="Decisions Needed Today"
              right={
                <span className="text-xs text-slate-500">
                  {decisions.length} decision(s)
                </span>
              }
            >
              <Table
                rows={decisions}
                empty="No urgent procurement decisions are currently flagged."
                columns={[
                  { key: "priority", label: "Priority" },
                  { key: "decision", label: "Decision Needed" },
                  { key: "reason", label: "Reason" },
                  { key: "owner", label: "Owner" },
                  { key: "action", label: "Recommended Action" },
                ]}
              />
            </Section>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Section
                title="Late Procurement"
                right={<Link to={ROUTES.PO_RECEIVING_DASH} className="text-xs text-cyan-300 hover:underline">Receiving →</Link>}
              >
                <Table
                  rows={late}
                  empty="No late procurement items found."
                  columns={[
                    { key: "po_number", label: "PO" },
                    { key: "vendor_name", label: "Vendor" },
                    { key: "expected_delivery", label: "Expected", render: (r) => String(r.expected_delivery || "—").slice(0, 10) },
                    { key: "status", label: "Status" },
                    { key: "total_cost", label: "Value", render: (r) => money(r.total_cost) },
                  ]}
                />
              </Section>

              <Section
                title="Blocked / Stalled POs"
                right={<Link to={ROUTES.PO_MANAGEMENT} className="text-xs text-cyan-300 hover:underline">PO Management →</Link>}
              >
                <Table
                  rows={blocked}
                  empty="No blocked or stalled POs found."
                  columns={[
                    { key: "po_number", label: "PO" },
                    { key: "vendor_name", label: "Vendor" },
                    { key: "status", label: "Status" },
                    { key: "block_reason", label: "Reason" },
                    { key: "total_cost", label: "Value", render: (r) => money(r.total_cost) },
                  ]}
                />
              </Section>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Section
                title="Risky Invoices"
                right={<Link to={ROUTES.INVOICES} className="text-xs text-cyan-300 hover:underline">Invoices →</Link>}
              >
                <Table
                  rows={riskyInvoices}
                  empty="No risky invoices found."
                  columns={[
                    { key: "po_number", label: "PO" },
                    { key: "vendor_name", label: "Vendor" },
                    { key: "invoice_total", label: "Invoice", render: (r) => money(r.invoice_total) },
                    { key: "po_total", label: "PO Total", render: (r) => money(r.po_total) },
                    { key: "variance", label: "Variance", render: (r) => money(r.variance) },
                  ]}
                />
              </Section>

              <Section
                title="Vendor Issues"
                right={<Link to={ROUTES.VENDORS} className="text-xs text-cyan-300 hover:underline">Vendors →</Link>}
              >
                <Table
                  rows={vendorIssues}
                  empty="No vendor issues found."
                  columns={[
                    { key: "vendor_name", label: "Vendor" },
                    { key: "issue_type", label: "Issue" },
                    { key: "count", label: "Count" },
                    { key: "risk_level", label: "Risk" },
                    { key: "recommendation", label: "Recommendation" },
                  ]}
                />
              </Section>
            </div>

            <Section title="PO Cost by Cost Centre">
              <Table
                rows={costCentres}
                empty="No cost centre procurement spend found."
                columns={[
                  { key: "cost_centre_name", label: "Cost Centre" },
                  { key: "po_count", label: "POs" },
                  { key: "total_value", label: "Total Value", render: (r) => money(r.total_value) },
                  { key: "pending_value", label: "Pending", render: (r) => money(r.pending_value) },
                  { key: "received_value", label: "Received", render: (r) => money(r.received_value) },
                ]}
              />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}